import { useEffect, useMemo, useState } from 'react';
import {
  Stack, Title, Group, Text, Card, ActionIcon, Menu, Skeleton, Alert, Box, ScrollArea, Badge, Tooltip, Checkbox,
} from '@mantine/core';
import {
  IconPlane, IconCar, IconBed, IconTrain, IconPencil, IconTrash, IconDots,
  IconCheckbox, IconCopy, IconClipboard, IconChecks, IconX,
} from '@tabler/icons-react';
import { useTravelClipboard } from '../state/travelClipboard.ts';
import { notifications } from '@mantine/notifications';
import {
  defaultsForAppendedLeg,
  normaliseLegForSave,
  sortLegs,
  splitDateTime,
  validateItinerary,
  type TravelLeg,
} from '../lib/itinerary.ts';
import {
  useTravelsByVisitor, useTravelsByGroup, useSaveTravel, useDeleteTravel,
} from '../api/hooks/useTravels.ts';
import type { Travel } from '../api/schemas/travel.ts';
import type { DraftLeg } from './ItineraryRow.tsx';
import { TravelEditModal } from './TravelEditModal.tsx';
import { useList } from '../api/hooks/useCrud.ts';
import type { Location as LocationDTO } from '../api/schemas/entities.ts';

/**
 * Right-column travel list. Each leg renders as a compact read-card
 * matching the legacy layout; clicking the pencil (or anywhere on the card)
 * opens the tabbed `TravelEditModal` (Plane / Train / Road / Accommodation).
 *
 * The `addLegSignal` prop is incremented by the page's "+ travel" FAB to
 * trigger a new-draft modal.
 */
export function TravelDetailsPanel({
  visitorId,
  groupId,
  addLegSignal,
  readOnly = false,
}: {
  visitorId?: number | null;
  groupId?: number | null;
  addLegSignal: number;
  // True when the visitor belongs to a group. Travel legs are then shared
  // via the group's owner and can't be edited on a per-visitor basis.
  readOnly?: boolean;
}) {
  // Either visitor- or group-scoped; only one of visitorId/groupId is set
  // by the parent. Group mode uses /api/travelsByGroup and persists travel
  // legs with groupId set instead of visitorId.
  const groupMode = groupId != null;
  const byVisitor = useTravelsByVisitor(groupMode ? null : visitorId ?? null);
  const byGroup = useTravelsByGroup(groupMode ? groupId : null);
  const list = groupMode ? byGroup : byVisitor;
  const save = useSaveTravel();
  const remove = useDeleteTravel();
  const locations = useList<LocationDTO>('locations');

  const [drafts, setDrafts] = useState<DraftLeg[]>([]);
  const [editing, setEditing] = useState<DraftLeg | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const clipboard = useTravelClipboard();

  // Resetting selection mode whenever the visitor/group target changes so
  // selections never leak across rows in the parent list.
  useEffect(() => {
    setSelectionMode(false);
    setSelectedKeys(new Set());
  }, [visitorId, groupId]);

  useEffect(() => {
    if (list.data) setDrafts(list.data.map(travelToDraft));
  }, [list.data, visitorId]);

  // Open a new draft modal when the parent FAB fires.
  useEffect(() => {
    const hasTarget = groupMode ? groupId != null : visitorId != null;
    if (!hasTarget || addLegSignal === 0 || readOnly) return;
    const prev = drafts[drafts.length - 1] ?? null;
    const defaults = defaultsForAppendedLeg(prev as TravelLeg | null);
    setEditing({
      draftId: `draft-${Date.now()}`,
      transport: 'plane',
      ...defaults,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addLegSignal]);

  const sorted = useMemo(() => sortLegs(drafts), [drafts]);
  const errors = useMemo(() => validateItinerary(sorted), [sorted]);
  const errorByIndex = useMemo(() => {
    const m = new Map<number, string>();
    for (const e of errors) m.set(e.legIndex, e.message);
    return m;
  }, [errors]);
  const overallError = errors.find(e => e.legIndex === 0 && /Madhuban/.test(e.message));

  // Core save: persists one leg and returns whether it succeeded. No toast, so
  // batch callers (paste) can report an accurate aggregate.
  async function persistLeg(next: DraftLeg): Promise<boolean> {
    const hasTarget = groupMode ? groupId != null : visitorId != null;
    if (!hasTarget) return false;
    const normalised = normaliseLegForSave(next);
    const payload = groupMode
      ? draftToGroupTravel(normalised, groupId!)
      : draftToTravel(normalised, visitorId!);
    try {
      const saved = await save.mutateAsync(payload);
      setDrafts(d => {
        if (next.id != null) {
          return d.map(x => (x.id === next.id ? travelToDraft(saved) : x));
        }
        return [...d.filter(x => x.draftId !== next.draftId), travelToDraft(saved)];
      });
      return true;
    } catch {
      return false;
    }
  }

  async function saveLeg(next: DraftLeg) {
    if (await persistLeg(next)) {
      setEditing(null);
      notifications.show({ message: 'Travel saved', color: 'green' });
    } else {
      notifications.show({ message: 'Save failed', color: 'red' });
    }
  }

  async function deleteLeg(target: DraftLeg) {
    if (target.id != null) {
      try { await remove.mutateAsync(target.id); }
      catch (err) {
        notifications.show({
          message: 'Delete failed' + (err instanceof Error ? `: ${err.message}` : ''),
          color: 'red',
        });
        return;
      }
    }
    setDrafts(d => d.filter(x => x !== target && (target.id == null || x.id !== target.id)));
    setEditing(null);
  }

  // Stable per-leg key for selection tracking. Saved legs have ids; drafts
  // fall back to their draftId.
  const keyOf = (leg: DraftLeg) =>
    leg.id != null ? `id:${leg.id}` : `draft:${leg.draftId ?? ''}`;

  function toggleSelected(leg: DraftLeg) {
    const k = keyOf(leg);
    setSelectedKeys(prev => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  }
  function selectAll() {
    setSelectedKeys(new Set(sorted.map(keyOf)));
  }
  function exitSelectionMode() {
    setSelectionMode(false);
    setSelectedKeys(new Set());
  }
  function copySelected() {
    const chosen = sorted.filter(l => selectedKeys.has(keyOf(l)));
    if (chosen.length === 0) return;
    // Strip identity fields so each item can be re-saved against any target.
    const templates = chosen.map(l => stripIdentity(l));
    clipboard.set(templates);
    notifications.show({
      message: `Copied ${chosen.length} leg${chosen.length === 1 ? '' : 's'}`,
      color: 'green',
    });
    exitSelectionMode();
  }
  async function pasteCopied() {
    if (clipboard.items.length === 0) return;
    let ok = 0;
    let failed = 0;
    for (const item of clipboard.items) {
      const fresh: DraftLeg = {
        ...item,
        draftId: `paste-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      };
      if (await persistLeg(fresh)) ok++;
      else failed++;
    }
    if (failed === 0) {
      notifications.show({ message: `Pasted ${ok} leg${ok === 1 ? '' : 's'}`, color: 'green' });
    } else {
      notifications.show({
        message: `Pasted ${ok}, ${failed} failed`,
        color: ok > 0 ? 'yellow' : 'red',
      });
    }
  }

  const hasTarget = groupMode ? groupId != null : visitorId != null;
  if (!hasTarget) {
    return (
      <Stack gap="md" h="100%">
        <Group justify="space-between">
          <Title order={4}>Travel Details</Title>
        </Group>
        <Text c="dimmed" size="sm">
          {groupMode ? 'Pick a group to view its travels.' : 'Pick a visitor to view their travels.'}
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="md" h="100%" style={{ minHeight: 0 }}>
      <Group justify="space-between">
        <Group gap="xs">
          <Title order={4}>Travel Details{groupMode ? ' (Group)' : ''}</Title>
          {readOnly && !groupMode && (
            <Tooltip label="This traveller is in a group — travel is managed at the group level" position="bottom" withArrow>
              <Badge color="grape" variant="light">Group</Badge>
            </Tooltip>
          )}
        </Group>
        <Menu shadow="md" width={220} position="bottom-end" closeOnItemClick>
          <Menu.Target>
            <ActionIcon variant="subtle" aria-label="Travel menu"><IconDots size={18} /></ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {!readOnly && (
              <>
                {!selectionMode ? (
                  <Menu.Item
                    leftSection={<IconCheckbox size={14} />}
                    onClick={() => setSelectionMode(true)}
                  >
                    Enter Copy/Paste Mode
                  </Menu.Item>
                ) : (
                  <>
                    <Menu.Item
                      leftSection={<IconX size={14} />}
                      onClick={exitSelectionMode}
                    >
                      Exit Copy/Paste Mode
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconChecks size={14} />}
                      onClick={selectAll}
                    >
                      Select All
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconCopy size={14} />}
                      disabled={selectedKeys.size === 0}
                      onClick={copySelected}
                    >
                      Copy ({selectedKeys.size})
                    </Menu.Item>
                  </>
                )}
                <Menu.Item
                  leftSection={<IconClipboard size={14} />}
                  disabled={clipboard.items.length === 0}
                  onClick={() => { void pasteCopied(); }}
                >
                  Paste ({clipboard.items.length})
                </Menu.Item>
                <Menu.Divider />
              </>
            )}
            <Menu.Item onClick={() => list.refetch()}>Refresh</Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      {overallError && (
        <Alert color="red" variant="light" radius="md">{overallError.message}</Alert>
      )}

      {list.isPending ? (
        <Stack gap="xs">{[1, 2, 3].map(i => <Skeleton key={i} h={72} radius="md" />)}</Stack>
      ) : (
        <Box style={{ flex: 1, minHeight: 0 }}>
          <ScrollArea h="100%" type="auto">
            <Stack gap="sm">
              {sorted.length === 0 && (
                <Text c="dimmed" size="sm">No travels yet. Tap the airplane button to add the first leg.</Text>
              )}
              {sorted.map((leg, i) => {
                const k = keyOf(leg);
                const selected = selectedKeys.has(k);
                const handleCardClick = () => {
                  if (readOnly) return;
                  if (selectionMode) toggleSelected(leg);
                  else setEditing(leg);
                };
                return (
                  <Card
                    key={leg.id ?? leg.draftId ?? i}
                    withBorder padding="sm" radius="md"
                    style={{
                      cursor: readOnly ? 'default' : 'pointer',
                      background: selectionMode && selected ? 'var(--mantine-color-blue-light)' : undefined,
                    }}
                    onClick={handleCardClick}
                  >
                    <Group wrap="nowrap" align="flex-start" gap="sm">
                      {selectionMode && !readOnly && (
                        <Checkbox
                          checked={selected}
                          onChange={() => toggleSelected(leg)}
                          onClick={e => e.stopPropagation()}
                          mt={6}
                        />
                      )}
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <ReadView
                          leg={leg}
                          locations={locations.data ?? []}
                          onEdit={() => setEditing(leg)}
                          onDelete={() => { void deleteLeg(leg); }}
                          error={errorByIndex.get(i)}
                          readOnly={readOnly || selectionMode}
                        />
                      </Box>
                    </Group>
                  </Card>
                );
              })}
            </Stack>
          </ScrollArea>
        </Box>
      )}

      <TravelEditModal
        leg={editing}
        onClose={() => setEditing(null)}
        onSave={saveLeg}
        onDelete={async () => { if (editing) await deleteLeg(editing); }}
      />
    </Stack>
  );
}

function ReadView({
  leg, locations, onEdit, onDelete, error, readOnly,
}: {
  leg: DraftLeg;
  locations: LocationDTO[];
  onEdit(): void;
  onDelete(): void;
  error?: string;
  readOnly?: boolean;
}) {
  const nameOf = (id: number | null | undefined) =>
    id == null ? '' : (locations.find(l => l.id === id)?.name ?? `#${id}`);
  const dep = splitDateTime(leg.departureDate ?? null);
  const arr = splitDateTime(leg.arrivalDate ?? null);
  const Icon = transportIcon(leg.transport);
  return (
    <Group justify="space-between" wrap="nowrap" align="flex-start">
      <Group gap="md" align="flex-start" wrap="nowrap" style={{ flex: 1 }}>
        <Icon size={28} stroke={1.5} style={{ flexShrink: 0, marginTop: 4 }} />
        <Stack gap={2} style={{ flex: 1 }}>
          <Group justify="space-between" gap="sm" wrap="wrap">
            <Stack gap={0}>
              <Text size="xs" c="dimmed">From</Text>
              <Text fw={500}>{nameOf(leg.departureFromId)}</Text>
              <Text size="xs" c="dimmed">{formatLocal(dep.date)} {leg.departureTime ?? dep.time ?? ''}</Text>
            </Stack>
            <Stack gap={0}>
              <Text size="xs" c="dimmed">To</Text>
              <Text fw={500}>{nameOf(leg.arrivalToId)}</Text>
              <Text size="xs" c="dimmed">{formatLocal(arr.date)} {leg.arrivalTime ?? arr.time ?? ''}</Text>
            </Stack>
          </Group>
          {leg.transportReference && <Badge variant="outline" size="sm">{leg.transportReference}</Badge>}
          {error && <Text size="xs" c="red" mt={4}>{error}</Text>}
        </Stack>
      </Group>
      {!readOnly && (
        <Group gap={0} onClick={e => e.stopPropagation()}>
          <ActionIcon variant="subtle" onClick={onEdit} aria-label="Edit"><IconPencil size={16} /></ActionIcon>
          <ActionIcon variant="subtle" color="red" onClick={onDelete} aria-label="Delete"><IconTrash size={16} /></ActionIcon>
        </Group>
      )}
    </Group>
  );
}

function transportIcon(t: string | undefined) {
  switch (t) {
    case 'plane': return IconPlane;
    case 'road': return IconCar;
    case 'train': return IconTrain;
    case 'stay': return IconBed;
    default: return IconPlane;
  }
}

function formatLocal(d: string | null): string {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  if (!y || !m || !day) return d;
  return `${day}/${m}/${y.slice(2)}`;
}

/**
 * Strip identity fields off a draft so it can be pasted into any target.
 * The new draftId gives the paste path a stable React key while the leg
 * is in-flight to the server.
 */
function stripIdentity(leg: DraftLeg): DraftLeg {
  const fresh = { ...leg };
  fresh.id = null;
  fresh.draftId = `clip-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  fresh.editing = false;
  return fresh;
}

function travelToDraft(t: Travel): DraftLeg {
  const dep = splitDateTime(t.departureDate ?? null);
  const arr = splitDateTime(t.arrivalDate ?? null);
  return {
    id: t.id ?? null,
    transport: (t.transport ?? 'plane') as DraftLeg['transport'],
    departureDate: t.departureDate ?? null,
    arrivalDate: t.arrivalDate ?? null,
    departureTime: dep.time,
    arrivalTime: arr.time,
    departureFromId: t.departureFromId ?? null,
    arrivalToId: t.arrivalToId ?? null,
    accomodationInId: t.accomodationInId ?? null,
    transportReference: t.transportReference ?? null,
    comment: t.comment ?? null,
    pickupRequired: t.pickupRequired ?? false,
    booked: t.booked ?? false,
    carType: t.carType ?? null,
    room: t.room ?? null,
    editing: false,
  };
}

function draftToTravel(d: DraftLeg, visitorId: number): Travel {
  return {
    ...baseTravel(d),
    visitorId,
  };
}

function draftToGroupTravel(d: DraftLeg, groupId: number): Travel {
  return {
    ...baseTravel(d),
    groupId,
  };
}

function baseTravel(d: DraftLeg): Travel {
  const merge = (date: string | null | undefined, time: string | null | undefined) => {
    if (!date) return null;
    if (date.includes('T')) return date;
    const t = (time && /^\d{2}:\d{2}/.test(time)) ? time.slice(0, 5) : '00:00';
    return `${date}T${t}:00`;
  };
  return {
    id: d.id ?? undefined,
    transport: d.transport,
    transportReference: d.transportReference ?? null,
    departureDate: merge(d.departureDate, d.departureTime),
    arrivalDate: merge(d.arrivalDate, d.arrivalTime),
    departureFromId: d.departureFromId ?? null,
    arrivalToId: d.arrivalToId ?? null,
    accomodationInId: d.accomodationInId ?? null,
    comment: d.comment ?? null,
    pickupRequired: d.pickupRequired ?? null,
    booked: d.booked ?? null,
    carType: d.carType ?? null,
    room: d.room ?? null,
  };
}
